import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import type { Validation } from "@imminently/decisively-core";
import Button from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import TextInput from "@imminently/decisively-ui/TextInput";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { useState } from "react";

export interface ValidationItemProps {
  validation: Validation;
  onTitleClick?: () => void;
}

const ValidationItem = (props: ValidationItemProps) => {
  const { validation, onTitleClick } = props;
  const isHidden = !validation.shown;

  return (
    <div
      key={`${validation.parent}-${validation.id}`}
      className={`validation-item ${validation.severity} px-3 py-3 m-0 border-l-4 border-transparent text-sm flex flex-col gap-0.5 ${
        isHidden
          ? "bg-gray-50"
          : validation.severity === "error"
            ? "border-l-red-500 bg-red-50"
            : "border-l-orange-500 bg-orange-50"
      } ${!isHidden ? "border-b border-border" : ""}`}
      style={{
        backgroundColor: isHidden ? "#f8f9fa" : undefined,
        borderLeftColor: isHidden ? (validation.severity === "error" ? "#e9ecef" : "#f8f9fa") : undefined,
      }}
    >
      {validation.parent ? (
        <small>
          <span
            title={validation.parent}
            className={`opacity-80 ${isHidden ? "text-gray-500" : ""} whitespace-nowrap overflow-hidden text-ellipsis max-w-80 inline-block`}
            style={{
              direction: "rtl",
            }}
          >
            {validation.parent}
          </span>
        </small>
      ) : null}
      <span>
        ({validation.severity}){" "}
        <span
          className={`${onTitleClick ? "cursor-pointer underline" : "cursor-default"} ${isHidden ? "text-gray-500" : ""}`}
          onClick={onTitleClick}
        >
          {validation.message}
        </span>
      </span>
    </div>
  );
};

export interface ValidationPanelProps {
  validations?: Validation[];
  className?: string;
  /** The validations are still being fetched. */
  loading?: boolean;
  /** Fetching the validations failed. */
  error?: string;
}

const ValidationPanel = (props: ValidationPanelProps) => {
  const { validations, className, loading, error } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [isHiddenExpanded, setIsHiddenExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { setSelectedNodeId } = useGraphVisualisation();

  // Split validations into shown and hidden
  const shownValidations = validations?.filter((validation) => validation.shown) || [];
  const hiddenValidations = validations?.filter((validation) => !validation.shown) || [];

  if (loading || error) {
    return (
      <div
        className={`${className ?? ""} flex items-center gap-2 px-3 py-1.5 text-xs rounded-full border shadow-sm ${
          error ? "border-red-200 bg-red-50 text-red-700" : "border-border bg-white text-gray-600"
        }`}
        role="status"
      >
        <Icon name={error ? "warning" : "refresh"} className={`size-3.5 ${error ? "" : "animate-spin"}`} />
        {error ?? "Checking validations…"}
      </div>
    );
  }

  if (!validations?.length) {
    return null;
  }

  const filterValidations = (validations: Validation[]) => {
    if (!searchTerm.trim()) {
      return validations;
    }

    const searchLower = searchTerm.toLowerCase();
    return validations.filter(
      (validation) =>
        validation.message.toLowerCase().includes(searchLower) ||
        (validation.parent && validation.parent.toLowerCase().includes(searchLower)),
    );
  };

  // Split validations by severity and filter by search term
  const filteredShownValidations = filterValidations(shownValidations);
  const filteredHiddenValidations = filterValidations(hiddenValidations);

  const shownErrors = filteredShownValidations.filter((v) => v.severity === "error");
  const shownWarnings = filteredShownValidations.filter((v) => v.severity === "warning");
  const hiddenErrors = filteredHiddenValidations.filter((v) => v.severity === "error");
  const hiddenWarnings = filteredHiddenValidations.filter((v) => v.severity === "warning");

  // Count validations
  const totalShownErrors = shownErrors.length;
  const totalShownWarnings = shownWarnings.length;
  const hasShownErrors = totalShownErrors > 0;
  const hasShownWarnings = totalShownWarnings > 0;

  const handleNodeFocus = (validation: Validation) => {
    if (validation.id) {
      setSelectedNodeId(validation.parent ? `${validation.parent}/${validation.id}` : validation.id);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const tooltipContent = (
    <div className="rounded-lg">
      {/* Search Bar */}
      <div className="px-3 py-2 border-b border-border relative">
        <TextInput
          placeholder="Search validations..."
          value={searchTerm}
          onValueChange={setSearchTerm}
          startAdornment={
            <Icon
              name="search"
              className="text-gray-400 text-base size-4"
            />
          }
          className="w-full text-xs"
          fullWidth
        />
      </div>

      {/* Shown Validations Section */}
      {(shownErrors.length > 0 || shownWarnings.length > 0) && (
        <>
          <div className="px-3 py-2 font-bold border-b border-border flex items-center justify-between">
            <span>Triggered Validations</span>
            <Button
              icon
              variant="ghost"
              intent="secondary"
              size="sm"
              label="Close"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700"
            >
              <Icon name="close" />
            </Button>
          </div>
          <div className="overflow-y-auto max-h-75">
            {shownErrors.map((validation, index) => (
              <ValidationItem
                validation={validation}
                key={`shown-error-${index}`}
                onTitleClick={() => handleNodeFocus(validation)}
              />
            ))}
            {shownWarnings.map((validation, index) => (
              <ValidationItem
                validation={validation}
                key={`shown-warning-${index}`}
                onTitleClick={() => handleNodeFocus(validation)}
              />
            ))}
          </div>
        </>
      )}

      {/* Hidden Validations Section */}
      {(hiddenErrors.length > 0 || hiddenWarnings.length > 0) && (
        <>
          <div
            className="px-3 py-2 font-bold border-b border-border cursor-pointer flex items-center justify-between"
            onClick={() => setIsHiddenExpanded(!isHiddenExpanded)}
          >
            Not Triggered Validations ({hiddenErrors.length + hiddenWarnings.length})
            {isHiddenExpanded ? <Icon name="collapse" /> : <Icon name="expand" />}
          </div>
          {isHiddenExpanded && (
            <div className="overflow-y-auto max-h-75">
              {hiddenErrors.map((validation, index) => (
                <ValidationItem
                  validation={validation}
                  key={`hidden-error-${index}`}
                  onTitleClick={() => handleNodeFocus(validation)}
                />
              ))}
              {hiddenWarnings.map((validation, index) => (
                <ValidationItem
                  validation={validation}
                  key={`hidden-warning-${index}`}
                  onTitleClick={() => handleNodeFocus(validation)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  // Determine chip color and content
  const getChipStyle = () => {
    if (hasShownErrors) {
      return "bg-red-100 text-red-600 border-red-200";
    } else if (hasShownWarnings) {
      return "bg-orange-100 text-orange-600 border-orange-200";
    } else {
      return "bg-gray-100 text-gray-600 border-border";
    }
  };

  const getChipContent = () => {
    const parts: React.ReactNode[] = [];

    parts.push(
      <span
        key="errors"
        className="inline-flex items-center gap-1"
      >
        <Icon name="error" />
        <span>
          {totalShownErrors} error{totalShownErrors === 1 ? "" : "s"}
        </span>
      </span>,
    );

    parts.push(
      <span
        key="warnings"
        className="inline-flex items-center gap-1"
      >
        <Icon name="warning" />
        <span>
          {totalShownWarnings} warning{totalShownWarnings === 1 ? "" : "s"}
        </span>
      </span>,
    );

    return <span className="inline-flex items-center gap-2">{parts}</span>;
  };

  return (
    <Tooltip
      visible={isOpen}
      onVisibleChange={setIsOpen}
      mode="click"
      interactable
      theme="light"
      placement="top"
    >
      <Tooltip.Trigger asChild>
        <div
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity duration-200 ${getChipStyle()} ${className || ""}`}
        >
          {getChipContent()}
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content className="max-w-[600px] p-0 text-[11px] font-inherit">{tooltipContent}</Tooltip.Content>
    </Tooltip>
  );
};

export default ValidationPanel;
